import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Filter,
  Package,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageWrapper from "../components/common/PageWrapper";
import { ProductCard } from "../components/common/ProductCard";
import FiltersSidebar from "../components/common/FiltersSidebar";
import FilterDrawer from "./common/FilterDrawer";
import { applyStorefrontFilters, activeStorefrontFilters, productPrice } from "../utils/storefrontFilters";
import { getAvailableVariants } from "../utils/storefrontStock";
import { apiRequest } from "../services/api";

const initialFilters = {
  categoryIds: [], subCategoryIds: [], brands: [], sizes: [], colors: [], discount: 0,
  minPrice: "",
  maxPrice: "",
  brand: "",
  size: "",
  color: "",
  rating: 0,
};

const getProductPrice = (product) => {
  const variants = product.variants || [];
  const availableVariants = getAvailableVariants(product);
  const variantsForPrice =
    availableVariants.length > 0 ? availableVariants : variants;

  const variant =
    variantsForPrice.find((item) => item.onlinePrice != null) ||
    variantsForPrice[0] ||
    {};

  return Number(
    product.price ??
      variant.onlinePrice ??
      variant.mrp ??
      0,
  );
};

const normalizeForFilters = (product) => ({
  ...product,
  price: productPrice(product),
  brand: product.brand?.name || product.brand || "",
  sizes: [
    ...new Set(
      getAvailableVariants(product)
        .map((variant) => variant.size?.name || variant.size?.code)
        .filter(Boolean),
    ),
  ],
  colors: [
    ...new Set(
      getAvailableVariants(product)
        .map((variant) => variant.color?.name)
        .filter(Boolean),
    ),
  ],
  rating: Number(product.rating || 0),
});

export default function CategoryPage() {
  const { catId } = useParams();
  const [searchParams] = useSearchParams();
  const subCategorySlug = searchParams.get('subCategory') || '';
  const navigate = useNavigate();
  const [filters, setFilters] = useState(initialFilters);
  const activeSubCategory = filters.subCategorySlug ?? subCategorySlug;
  const [catalogueCategories, setCatalogueCategories] = useState([]);
  useEffect(() => {
    let active = true;
    apiRequest('/storefront/categories').then(data => { if (active) setCatalogueCategories(data || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const category = useMemo(
    () => {
      const parent = catalogueCategories.find(item => item.id === catId || item.slug === catId);
      const child = parent?.subCategories?.find(item => item.slug === activeSubCategory);
      return parent ? { ...parent, name: child?.name || parent.name } : { id: catId, name: 'Collection' };
    },
    [catId, catalogueCategories, activeSubCategory],
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadCategoryProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const categorySlug = category.slug || category.id;

        const response = await apiRequest(
          `/storefront/products?category=${encodeURIComponent(categorySlug)}${activeSubCategory ? `&subCategory=${encodeURIComponent(activeSubCategory)}` : ''}`,
        );

        if (mounted) {
          setProducts(response || []);
        }
      } catch (requestError) {
        console.error("Could not load category products:", requestError);

        if (mounted) {
          setProducts([]);
          setError(
            requestError.message ||
              "Could not load this collection. Please try again.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCategoryProducts();

    return () => {
      mounted = false;
    };
  }, [category.id, category.slug, activeSubCategory]);

  const items = useMemo(() => {
    let result = products.map(normalizeForFilters);

    result = applyStorefrontFilters(result, filters);

    if (sort === "price_asc") {
      result = [...result].sort((first, second) => first.price - second.price);
    }

    if (sort === "price_desc") {
      result = [...result].sort((first, second) => second.price - first.price);
    }

    if (sort === "rating") {
      result = [...result].sort(
        (first, second) => second.rating - first.rating,
      );
    }

    if (sort === "newest") {
      result = [...result].sort(
        (first, second) =>
          new Date(second.createdAt || 0) - new Date(first.createdAt || 0),
      );
    }

    return result;
  }, [products, filters, sort]);

  const activeFilterCount = activeStorefrontFilters(filters);

  const clearFilters = () => {
    setFilters({ ...initialFilters, subCategorySlug: '' });
    setShowFilters(false);
  };

  return (
    <PageWrapper>
      {/* Collection hero */}
      <section className="border-b border-[#eadde1] bg-[#faf7f5]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
            CHARMS Collection
          </p>

          <div className="mt-3 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-4xl font-semibold tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-6xl">
                {category.name}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
                Discover premium styles from the brands you love, curated for
                every occasion.
              </p>
            </div>

            <div className="border-l-2 border-primary-800 pl-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                Available online
              </p>

              <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
                {loading ? "—" : items.length}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-7 border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {/* Collection controls */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3 border-y border-slate-200 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-900 transition hover:border-primary-800 hover:text-primary-800 lg:hidden"
            >
              <Filter size={16} />
              Filters
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>

            <div className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 lg:flex">
              <SlidersHorizontal size={15} />
              {loading ? "Loading collection" : `${items.length} products`}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="category-product-sort"
              className="hidden text-xs font-bold uppercase tracking-[0.12em] text-slate-500 sm:block"
            >
              Sort by
            </label>

            <select
              id="category-product-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="border border-slate-300 bg-white px-3 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-slate-800 outline-none transition focus:border-primary-800"
            >
              <option value="newest">Newest</option>
              <option value="rating">Top Rated</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Mobile filters */}
        <FilterDrawer open={showFilters} onClose={()=>setShowFilters(false)} onClear={()=>setFilters({...initialFilters,subCategorySlug:''})} count={items.length}>
          <FiltersSidebar showHeader={false} categories={catalogueCategories} onCategoryChange={value=>navigate(value?`/category/${value}`:"/search/all")} filters={{...filters,categoryId:category.id,subCategorySlug:activeSubCategory}} setFilters={setFilters} products={products} />
        </FilterDrawer>

        <div className="flex gap-10">
          {/* Desktop filters */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-32 max-h-[calc(100dvh-9rem)] overflow-y-auto overscroll-contain rounded-2xl bg-slate-50/70 p-3">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-950">Filters</h2>

                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-bold uppercase tracking-[0.12em] text-primary-800 transition hover:text-primary-950"
                  >
                    Clear all
                  </button>
                ) : null}
              </div>

              <FiltersSidebar showHeader={false}
                categories={catalogueCategories}
                onCategoryChange={value=>navigate(value?`/category/${value}`:"/search/all")}
                filters={{...filters,categoryId:category.id,subCategorySlug:activeSubCategory}}
                setFilters={setFilters}
                products={products}
              />
            </div>
          </aside>

          {/* Product listing */}
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="aspect-[3/4] animate-pulse bg-slate-100"
                  />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex min-h-[450px] flex-col items-center justify-center border border-dashed border-slate-300 bg-[#faf7f5] px-6 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-primary-800">
                  <Package size={23} />
                </div>

                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
                  No products found
                </h3>

                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try changing your filters or explore the complete CHARMS
                  collection.
                </p>

                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="border border-slate-900 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-white"
                  >
                    Clear filters
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/search/all")}
                    className="inline-flex items-center gap-2 bg-primary-800 px-5 py-3 text-sm font-bold text-white transition hover:bg-primary-950"
                  >
                    Browse all products
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
                {items.map((item, index) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    index={index}
                    navigate={navigate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
